/**
 * useTaskSubmissionController
 *
 * Orchestrates side effects for submission authoring: save, submit, evaluate.
 * Reads/writes to TaskSubmissionStore. Editor- and RHF-agnostic.
 */

import React from 'react';
import { Platform, Keyboard } from 'react-native';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react-native';
import {
  EXTENDED_WAIT_THRESHOLD_MS,
  useTaskSubmissionStore,
  type EvaluationStreamProgress,
} from '../stores/TaskSubmissionStore';
import {
  containsImages,
  validateSubmission,
  promptValidationConfirmations,
} from '../../../common/utils/submissionValidation';
import { TaskTypeEnum } from '../../../common/api/models/taskTypeEnum';
import { TaskFormalCriteria } from '../../../common/api/models/taskFormalCriteria';
import {
  CreateTaskSubmissionMutationError,
} from '../../../common/api/endpoints/submissions/submissions';
import { GenericApiResponseSubmission } from '../../../common/api/models/genericApiResponseSubmission';
import { useUpdateSubmission } from '../../../common/hooks/student/task/useUpdateSubmission';
import { useActivity } from '../../../common/hooks/student/useActivity';
import { useDialog } from '../../../common/context/DialogContext';
import { useTaskSelectionState } from '../../../common/context/AppStateStore';
import { useEvaluationStream } from '../../../common/hooks/useEvaluationStream';
import { useNetworkStatus } from '../../../common/hooks/useNetworkStatus';
import type { UUID } from '../../../common/types/ids';
import { isUUID, areAllUUIDs } from '../../../common/types/ids';
import { processApiError } from '../../../common/utils/apiErrorReporter';
import log from '../../../common/utils/logger';
import { TaskScope } from '../types';

/** Client bound for student Evaluate; above the server's ~240s stream deadline. */
export const STUDENT_EVALUATION_TIMEOUT_MS = 250_000;

const AI_TUTOR_BREADCRUMB_CATEGORY = 'ai-tutor';

type CoarseElapsedBucket = '45s-90s' | '90s-180s' | '180s+';

function getCoarseElapsedBucket(elapsedMs: number): CoarseElapsedBucket {
  if (elapsedMs <= 90_000) return '45s-90s';
  if (elapsedMs <= 180_000) return '90s-180s';
  return '180s+';
}

function addExtendedWaitCancelBreadcrumb(
  taskId: string,
  progress: EvaluationStreamProgress,
): void {
  Sentry.addBreadcrumb({
    category: AI_TUTOR_BREADCRUMB_CATEGORY,
    type: 'user',
    level: 'info',
    message: 'evaluation_extended_wait_cancel',
    data: {
      taskId,
      reason: 'extended_wait_cancel',
      progressStage: progress.stage,
      elapsedBucket: getCoarseElapsedBucket(progress.elapsedMs),
    },
  });
}

function addEvaluationErrorRetryBreadcrumb(taskId: string): void {
  Sentry.addBreadcrumb({
    category: AI_TUTOR_BREADCRUMB_CATEGORY,
    type: 'user',
    level: 'info',
    message: 'evaluation_error_retry',
    data: {
      taskId,
      reason: 'evaluation_error_retry',
    },
  });
}

export interface UseTaskSubmissionControllerArgs {
  taskScope: TaskScope;
  formalReq?: TaskFormalCriteria;
  taskType: TaskTypeEnum;
  /**
   * Whether to enable autosave/resume logic in this instance.
   * CRITICAL: Set to false for all instances except the primary editor (EssayComposer).
   * Multiple active autosave instances will cause race conditions and 409 errors on resume.
   * @default true
   */
  enableAutosave?: boolean;
}

export interface TaskSubmissionController {
  saveDraft: (
    content?: string,
    opts?: { silent?: boolean; showErrorDialog?: boolean; isRetry?: boolean; isBackground?: boolean }
  ) => Promise<void>;
  submit: (content?: string) => Promise<void>;
  evaluate: () => Promise<void>;
  cancelEvaluation: () => void;
}

export function useTaskSubmissionController(args: UseTaskSubmissionControllerArgs): TaskSubmissionController {
  const {
    taskScope, taskType, formalReq, enableAutosave = true,
  } = args;
  const updateSubmissionMutation = useUpdateSubmission(taskScope);
  const { logCloseTask } = useActivity();
  const { showDialog, showToastMessage } = useDialog();
  const { t } = useTranslation();

  // Only call hook if moduleId is a valid UUID, otherwise pass undefined
  const validModuleId = taskScope?.moduleId && isUUID(taskScope.moduleId) ? taskScope.moduleId as UUID : undefined;
  const { setSelectedTask } = useTaskSelectionState(validModuleId);

  const { isOffline } = useNetworkStatus();
  const setOffline = useTaskSubmissionStore((s) => s.setOffline);

  // Diagnostic: ID to track this specific hook instance across re-renders/remounts
  const instanceId = React.useMemo(() => Math.random().toString(36).substr(2, 6), []);

  React.useEffect(() => {
    setOffline(isOffline);
  }, [isOffline, setOffline]);

  const html = useTaskSubmissionStore((s) => s.submission.html);
  const wordcount = useTaskSubmissionStore((s) => s.submission.wordcount);
  const setAutosaveState = useTaskSubmissionStore((s) => s.setAutosaveState);
  const setLastSavedAt = useTaskSubmissionStore((s) => s.setLastSavedAt);
  const setDirty = useTaskSubmissionStore((s) => s.setDirty);
  const setReadiness = useTaskSubmissionStore((s) => s.setReadiness);
  const recomputeCanSubmit = useTaskSubmissionStore((s) => s.recomputeCanSubmit);
  const evaluationUrl = React.useMemo(
    () => `/courses/${taskScope.courseId}/modules/${taskScope.moduleId}/tasks/${taskScope.taskId}/ai-tutor/evaluation`,
    [taskScope.courseId, taskScope.moduleId, taskScope.taskId],
  );
  const { startEvaluation, stopEvaluation } = useEvaluationStream({
    timeoutMs: STUDENT_EVALUATION_TIMEOUT_MS,
    onComplete: (payload) => {
      setReadiness(payload.isReadyToProgress);
      recomputeCanSubmit();
      if (payload.message) {
        showToastMessage('info', payload.message, '', 'top', 8000);
      }
    },
  });
  const dirty = useTaskSubmissionStore((s) => s.submission.dirty);
  const autosaveStatus = useTaskSubmissionStore((s) => s.autosave.status);
  const autosaveErrorCode = useTaskSubmissionStore((s) => s.autosave.errorCode);

  // ETag management: Use Zustand store (shared across all instances, survives remounts)
  // The store's etag is updated via setEtag when we save, and read directly when sending
  const setEtag = useTaskSubmissionStore((s) => s.setEtag);

  // Request queue for serialization: ensures Request 2 waits for Request 1's ETag
  const requestQueueRef = React.useRef<Promise<void>>(Promise.resolve());

  // Use ref for html to avoid recreating saveDraft on every keystroke
  const htmlRef = React.useRef<string>(html);
  React.useEffect(() => {
    htmlRef.current = html;
  }, [html]);

  // Helper to clean ETag (strip quotes from Cosmos DB format)
  const cleanEtag = (etag: string | null): string | null => {
    if (!etag) return null;
    return etag.replace(/^["']|["']$/g, '');
  };

  // Update ETag in store when we receive a response from server
  const updateStoreEtag = React.useCallback((newEtag: string | null) => {
    if (newEtag) {
      const cleanedEtag = cleanEtag(newEtag);
      const currentEtag = useTaskSubmissionStore.getState().submission.etag;

      if (cleanedEtag !== currentEtag) {
        setEtag(cleanedEtag);
      }
    }
  }, [setEtag]);

  const saveDraft = React.useCallback(
    async (
      content?: string,
      opts: { silent?: boolean; showErrorDialog?: boolean; isRetry?: boolean; isBackground?: boolean } = {
        silent: true,
        showErrorDialog: true,
        isRetry: false,
        isBackground: false,
      },
    ) => {
      if (isOffline) {
        log.warn('[TaskSubmissionController:Save] Aborted - Device is offline');
        const msg = t('student.task.composer.messages.submission-save-error-offline');
        setAutosaveState('error', msg);

        if (opts.showErrorDialog !== false) {
          showDialog({
            title: t('errors.connection_lost'),
            message: msg,
            buttons: [{ text: t('common.buttons.ok') }],
          });
        }
        throw new Error(msg);
      }

      const text = content ?? htmlRef.current ?? '';
      if (!taskScope?.taskId) {
        log.warn('[TaskSubmissionController:Save] Aborted - taskId is missing in taskScope');
        return Promise.resolve();
      }

      // Validate IDs are proper strings, not literal "undefined" or "null"
      if (!areAllUUIDs(taskScope.courseId, taskScope.moduleId, taskScope.taskId)) {
        const msg = t('student.task.composer.messages.submission-save-error-invalid-ids')
          || 'Cannot save submission: Invalid course, module, or task selection. Please refresh the page.';

        log.error('[TaskSubmissionController:Save] Aborted - Invalid IDs detected', {
          courseId: taskScope.courseId,
          moduleId: taskScope.moduleId,
          taskId: taskScope.taskId,
        });

        setAutosaveState('error', msg, 'INVALID_IDS');

        if (opts.showErrorDialog !== false) {
          showDialog({
            title: t('errors.invalid_state') || 'Invalid State',
            message: msg,
            buttons: [{ text: t('common.buttons.ok') }],
          });
        }

        throw new Error(msg);
      }

      if (containsImages(text)) {
        log.warn('[TaskSubmissionController:Save] Aborted - Submission contains local images (not supported)');
        if (!opts.silent) {
          showDialog({
            title: t('student.task.composer.messages.submission-save-error-noimage-dialog.title'),
            message: t('student.task.composer.messages.submission-save-error-noimage-dialog.message'),
            buttons: [{ text: t('common.buttons.ok') }],
          });
        }
        return Promise.resolve();
      }

      // Serialize requests: chain this save to the end of the queue
      const executeRequest = async (): Promise<void> => {
        const callId = Math.random().toString(36).substr(2, 6);
        const currentEtag = useTaskSubmissionStore.getState().submission.etag;

        try {
          if (!opts.isBackground) {
            setAutosaveState('inflight');
          }

          const resp = await updateSubmissionMutation.mutateAsync({
            courseId: taskScope.courseId,
            moduleId: taskScope.moduleId,
            taskId: taskScope.taskId,
            data: { text, state: 'draft', lastKnownEtag: currentEtag },
            // Biopsy Headers (via query params) for production diagnosis
            params: {
              _diag_inst: instanceId,
              _diag_req: Math.random().toString(36).substr(2, 6),
              _diag_src: opts.isRetry ? 'retry' : 'autosave',
              _diag_call: callId,
            },
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          } as any);

          const currentStoreTaskId = useTaskSubmissionStore.getState().submission.taskId;
          if (currentStoreTaskId && currentStoreTaskId !== taskScope.taskId) {
            log.warn(`[TaskSubmissionController:${instanceId}] Save ABORTED (callId: ${callId}) - Task switched`, {
              expected: taskScope.taskId,
              actual: currentStoreTaskId,
            });
            return;
          }

          const payload = resp?.data?.data;
          const newEtag = payload?.etag;
          if (newEtag) {
            updateStoreEtag(newEtag);
          }
          if (payload?.etagReconciledFromStaleClient) {
            Sentry.addBreadcrumb({
              category: 'submission',
              type: 'http',
              level: 'info',
              message: 'Server reconciled stale lastKnownEtag (concurrent activity)',
              data: {
                taskId: taskScope.taskId,
                callId,
                diagSource: opts.isRetry ? 'retry' : 'autosave',
              },
            });
          }

          setAutosaveState('saved');
          setLastSavedAt(Date.now());
          setDirty(false);
          if (!opts.silent) showToastMessage('success', t('student.task.composer.messages.submission-save-success'));
        } catch (error) {
          const currentStoreTaskId = useTaskSubmissionStore.getState().submission.taskId;
          if (currentStoreTaskId && currentStoreTaskId !== taskScope.taskId) {
            log.warn(`[TaskSubmissionController:${instanceId}] Save ERROR IGNORED (callId: ${callId}) - Task switched`);
            return;
          }

          const typedError = error as CreateTaskSubmissionMutationError;
          const errorData = typedError?.response?.data as GenericApiResponseSubmission;

          const isConcurrentError = typedError?.response?.status === 409
            || errorData?.error?.code === 'SUBMISSION_MODIFIED_CONCURRENTLY';

          if (isConcurrentError) {
            const details = errorData?.error?.details as { etag?: string; current_etag?: string } | undefined;
            const serverEtag = details?.etag || details?.current_etag;

            log.warn(`[TaskSubmissionController:${instanceId}] Save CONFLICT (callId: ${callId})`, {
              clientEtag: currentEtag,
              serverEtag,
              canRetry: !!(serverEtag && !opts.isRetry),
            });

            if (serverEtag && !opts.isRetry) {
              log.warn(`[TaskSubmissionController:${instanceId}] Retrying save with server ETag: ${serverEtag}`);
              setEtag(cleanEtag(serverEtag));
              await saveDraft(content, { ...opts, isRetry: true });
              return;
            }

            log.error(`[TaskSubmissionController:${instanceId}] Save FAILED - Concurrency conflict (callId: ${callId})`, error);
            const msg = errorData?.error?.message
              || t('student.task.composer.messages.submission-concurrent-error')
              || 'Your submission was modified elsewhere. Please refresh.';
            setAutosaveState('error', msg, 'SUBMISSION_MODIFIED_CONCURRENTLY');

            if (opts.showErrorDialog !== false) {
              showDialog({
                title: t('student.task.composer.messages.submission-concurrent-error-title') || 'Save Failed',
                message: msg,
                buttons: [
                  { text: t('common.buttons.cancel') || 'Cancel', variant: 'outline', action: 'secondary' },
                  {
                    text: t('common.buttons.retry') || 'Retry',
                    onPress: () => saveDraft(content, { ...opts, silent: false }),
                  },
                ],
              });
            }
            throw error;
          }

          const isForbiddenError = typedError?.response?.status === 403;
          const errorCode = errorData?.error?.code;

          const isValidationError = typedError?.response?.status === 400
            || typedError?.response?.status === 413
            || typedError?.response?.status === 422;

          if (isForbiddenError) {
            log.warn(`[TaskSubmissionController:${instanceId}] Save FORBIDDEN (callId: ${callId}) - 403 Permission Denied`, error);
            const msg = errorData?.error?.message
              || t('student.task.composer.messages.submission-forbidden-error')
              || 'You no longer have permission to edit this submission.';
            setAutosaveState('error', msg, 'FORBIDDEN');

            if (opts.showErrorDialog !== false) {
              showDialog({
                title: t('student.task.composer.messages.submission-forbidden-error-title') || 'Permission Denied',
                message: msg,
                buttons: [{ text: t('common.buttons.ok') || 'OK' }],
              });
            }
            throw error;
          }

          if (isValidationError) {
            log.warn(`[TaskSubmissionController:${instanceId}] Save FAILED - Validation error (callId: ${callId})`, {
              message: error.message,
              response: errorData,
            });
          } else {
            log.error(`[TaskSubmissionController:${instanceId}] Save FAILED - General error (callId: ${callId})`, error);
          }
          const msg = errorData?.error?.message || t('student.task.composer.messages.submission-save-error');
          setAutosaveState('error', msg, errorCode);

          // For validation errors, we want to show the dialog even for autosave if possible,
          // so the user knows they need to fix their content
          if (opts.showErrorDialog !== false || isValidationError) {
            showDialog({
              title: isValidationError ? t('errors.validation_error', { defaultValue: 'Validation Error' }) : t('errors.save_failed'),
              message: isValidationError ? msg : t('student.task.composer.messages.submission-save-failed-dialog'),
              buttons: [
                { text: t('common.buttons.cancel') || 'Cancel', variant: 'outline', action: 'secondary' },
                {
                  text: t('common.buttons.retry') || 'Retry',
                  onPress: () => saveDraft(content, { ...opts, silent: false, showErrorDialog: true }),
                },
              ],
            });
          }
          throw error;
        }
      };

      requestQueueRef.current = requestQueueRef.current.then(executeRequest).catch(() => {
        // Errors are already handled in executeRequest
      });

      return requestQueueRef.current;
    },
    [
      // html removed - using htmlRef instead to prevent recreation on every keystroke
      taskScope,
      setAutosaveState,
      updateStoreEtag,
      updateSubmissionMutation,
      showDialog,
      showToastMessage,
      t,
      isOffline,
      setDirty,
      setEtag,
      setLastSavedAt,
      instanceId,
    ],
  );

  // Resume Strategy:
  // 1. Immediate Resume: When we come back online, save immediately if dirty.
  React.useEffect(() => {
    const isUnrecoverableError = autosaveErrorCode === 'SUBMISSION_MODIFIED_CONCURRENTLY'
      || autosaveErrorCode === 'SUBMISSION_INLINE_IMAGES_NOT_ALLOWED'
      || autosaveErrorCode === 'SUBMISSION_CONTENT_TOO_LARGE'
      || autosaveErrorCode === 'INVALID_IDS'
      || autosaveErrorCode === 'FORBIDDEN';

    if (
      enableAutosave
      && !isOffline
      && dirty
      && autosaveStatus === 'error'
      && !isUnrecoverableError
    ) {
      // We are back online, and have unsaved changes that previously failed.
      // Retry immediately, but suppress dialogs to avoid interruption if it's just a blip.
      saveDraft(undefined, {
        silent: true, showErrorDialog: false, isBackground: true,
      }).catch(() => {
        // If it fails again, it will stay in error state, which is correct.
      });
    }
  }, [isOffline, dirty, autosaveStatus, autosaveErrorCode, enableAutosave]); // eslint-disable-line react-hooks/exhaustive-deps

  // 2. Safety Net Poller: Removed as per new requirement.
  // We no longer retry automatically in the background when in error state.
  // Instead, we show a retry button to the user.
  React.useEffect(() => {
    // interval retry logic removed
  }, [autosaveStatus, dirty, autosaveErrorCode]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = React.useCallback(
    async (content?: string) => {
      const executeRequest = async (): Promise<void> => {
        try {
          if (Platform.OS !== 'web') {
            Keyboard.dismiss();
          }
          const text = content ?? html ?? '';
          if (!taskScope?.taskId) {
            log.warn('[TaskSubmissionController:Submit] Aborted - taskId is missing in taskScope');
            return;
          }
          if (containsImages(text)) {
            log.warn('[TaskSubmissionController:Submit] Aborted - Submission contains local images (not supported)');
            showDialog({
              title: t('student.task.composer.messages.submission-save-error-noimage-dialog.title'),
              message: t('student.task.composer.messages.submission-save-error-noimage-dialog.message'),
              buttons: [{ text: t('common.buttons.ok') }],
            });
            return;
          }
          if (taskType === TaskTypeEnum.essay) {
            const validation = validateSubmission(text, wordcount?.bodyWords ?? 0, formalReq);
            if (validation.validations.length > 0) {
              const shouldProceed = await promptValidationConfirmations(validation.validations, showDialog, t);
              if (!shouldProceed) return;
            }
          }
          // Get ETag from store (shared across all instances) INSIDE the enqueued execution
          // to ensure we read the latest ETag after any pending autosaves complete
          const submitEtag = useTaskSubmissionStore.getState().submission.etag;

          const submitResp = await updateSubmissionMutation.mutateAsync({
            courseId: taskScope.courseId,
            moduleId: taskScope.moduleId,
            taskId: taskScope.taskId,
            data: {
              text,
              state: 'marked_as_done',
              lastKnownEtag: submitEtag,
            },
          });

          const submitPayload = submitResp?.data?.data;
          const newEtag = submitPayload?.etag;
          if (newEtag) {
            updateStoreEtag(newEtag);
          }
          if (submitPayload?.etagReconciledFromStaleClient) {
            Sentry.addBreadcrumb({
              category: 'submission',
              type: 'http',
              level: 'info',
              message: 'Server reconciled stale lastKnownEtag on mark-as-done',
              data: { taskId: taskScope.taskId },
            });
          }

          await logCloseTask(taskScope);

          // Validate moduleId before calling setSelectedTask
          if (taskScope.moduleId && isUUID(taskScope.moduleId)) {
            setSelectedTask(taskScope.moduleId, null);
          }

          showToastMessage('success', t('student.task.composer.messages.submission-save-submit-success'));
        } catch (error) {
          const { code, message } = processApiError(error);
          // Backend completion gate (thresholds / draft fingerprint / manual-release)
          // is an expected validation path: keep the recovery dialog, do not file Sentry.
          if (code === 'TASK_COMPLETION_GATE_FAILED') {
            log.warn(
              '[TaskSubmissionController:Submit] Mark-as-done rejected by completion gate',
              code,
              message,
            );
          } else {
            log.error('[TaskSubmissionController:Submit] Failed - Error during final submission', error);
          }
          showDialog({
            title: t('errors.error_title', { defaultValue: 'Error' }),
            message,
            buttons: [{ text: t('common.buttons.ok') }],
          });
        }
      };

      requestQueueRef.current = requestQueueRef.current.then(executeRequest).catch(() => {
        // Errors are already handled in executeRequest
      });

      return requestQueueRef.current;
    },
    [
      html,
      taskScope,
      wordcount?.bodyWords,
      updateStoreEtag,
      showDialog,
      t,
      updateSubmissionMutation,
      logCloseTask,
      setSelectedTask,
      showToastMessage,
      taskType,
      formalReq,
    ],
  );

  const evaluate = React.useCallback(async () => {
    try {
      const { evaluation, submission } = useTaskSubmissionStore.getState();
      if (evaluation.error && taskScope?.taskId) {
        addEvaluationErrorRetryBreadcrumb(taskScope.taskId);
      }
      if (submission.dirty) {
        await saveDraft(undefined, { silent: false });
      }
      startEvaluation(evaluationUrl);
    } catch (error) {
      log.error('[TaskSubmissionController:Evaluate] Failed before stream start', error);
      const { message } = processApiError(error);
      showDialog({
        title: t('errors.error_title', { defaultValue: 'Error' }),
        message,
        buttons: [{ text: t('common.buttons.ok') }],
      });
    }
  }, [evaluationUrl, saveDraft, showDialog, startEvaluation, t, taskScope?.taskId]);

  const cancelEvaluation = React.useCallback(() => {
    const { evaluation: { progress } } = useTaskSubmissionStore.getState();
    if (
      taskScope?.taskId
      && progress
      && progress.elapsedMs > EXTENDED_WAIT_THRESHOLD_MS
    ) {
      addExtendedWaitCancelBreadcrumb(taskScope.taskId, progress);
    }
    stopEvaluation();
  }, [stopEvaluation, taskScope?.taskId]);

  return {
    saveDraft,
    submit,
    evaluate,
    cancelEvaluation,
  };
}

export default useTaskSubmissionController;
